import {
  morningRoutine,
  weekendMorning,
  mealsBlock,
  napBlock,
  toddlerBedtime,
  windDown,
  type Task,
} from "./blocks";
import { FULL_DAYS, type FullDay } from "./days";
import { timeToMins } from "../lib/dateUtils";

export interface DaySchedule {
  emoji: string;
  theme: string;
  tasks: Task[];
}

// Raw schedule keyed by day name. Tasks get IDs + sort by time below.
const rawSchedule: Record<FullDay, DaySchedule> = {
  Monday: {
    emoji: "🌿", theme: "Fresh Start",
    tasks: [
      ...morningRoutine(true),
      { cat: "💪 Workout",      label: "Stroller walk (your cardio)",            time: "9:00 AM",  duration: "20–30 min", personal: true },
      { cat: "🧺 Laundry",      label: "Toddler's laundry — start, swap & fold", time: "9:30 AM",  duration: "~45 min" },
      { cat: "🛁 Bathroom",     label: "Clean the bathroom",                     time: "10:30 AM", duration: "~20 min" },
      ...mealsBlock(),
      ...napBlock(),
      { cat: "💇 Hair",         label: "Wash your hair (alternating Mon/Thu)",   time: "12:30 PM", duration: "~30 min", personal: true, trackId: "hairwash" },
      { cat: "💪 Workout",      label: "30-min workout",                          time: "4:30 PM",  duration: "30 min", personal: true },
      { cat: "🌳 Outdoor Play", label: "Outside time & best friend call",        time: "5:00 PM",  duration: "30–45 min", personal: true },
      ...toddlerBedtime(),
      ...windDown(false, true),
    ],
  },

  Tuesday: {
    emoji: "✨", theme: "Shine On",
    tasks: [
      ...morningRoutine(true),
      { cat: "💪 Workout",        label: "Stroller walk (your cardio)",            time: "9:00 AM",  duration: "20–30 min", personal: true },
      { cat: "🛏 Bedroom",        label: "Dust & wipe surfaces, vacuum floors, wipe mirrors", time: "9:30 AM", duration: "~25 min" },
      ...mealsBlock(),
      ...napBlock(),
      { cat: "🌱 Personal Growth", label: "Personal growth time — course, hobby, reading or passion project", time: "1:00 PM", duration: "30 min", personal: true },
      { cat: "👶 Toddler Dev",    label: "Toddler playdate or mommy-and-me outing", time: "2:30 PM",  duration: "1–2 hrs", personal: true },
      { cat: "🌳 Outdoor Play",   label: "Outside time with toddler",              time: "5:00 PM",  duration: "30–45 min", personal: true },
      ...toddlerBedtime(),
      ...windDown(true, true),
    ],
  },

  Wednesday: {
    emoji: "🏡", theme: "Heart of Home",
    tasks: [
      ...morningRoutine(true),
      { cat: "💪 Workout",      label: "Stroller walk (your cardio)",            time: "9:00 AM",  duration: "20–30 min", personal: true },
      { cat: "🍳 Kitchen",      label: "Wipe counters & stovetop, clean microwave & appliances, mop floor, empty trash", time: "9:30 AM", duration: "~35 min" },
      ...mealsBlock(),
      ...napBlock(),
      { cat: "💪 Workout",      label: "30-min workout",                         time: "4:30 PM",  duration: "30 min", personal: true },
      { cat: "🌳 Outdoor Play", label: "Outside time with toddler",              time: "5:00 PM",  duration: "30–45 min", personal: true },
      ...toddlerBedtime(),
      ...windDown(false, true),
    ],
  },

  Thursday: {
    emoji: "🌸", theme: "Flow Through",
    tasks: [
      ...morningRoutine(true),
      { cat: "💪 Workout",      label: "Stroller walk (your cardio)",            time: "9:00 AM",  duration: "20–30 min", personal: true },
      { cat: "🛋 Living Room",  label: "Dust shelves & surfaces, vacuum floors, fluff pillows & throws", time: "9:30 AM", duration: "~25 min" },
      { cat: "🍽 Dining Room",  label: "Wipe table & chairs, sweep & mop floor", time: "10:00 AM", duration: "~15 min" },
      ...mealsBlock(),
      ...napBlock(),
      { cat: "💇 Hair",         label: "Wash your hair (alternating Mon/Thu)",   time: "12:30 PM", duration: "~30 min", personal: true, trackId: "hairwash" },
      { cat: "🔗 Connection",   label: "Toddler video call with family",         time: "5:00 PM",  duration: "15–20 min", personal: true },
      { cat: "🌳 Outdoor Play", label: "Outside time with toddler",              time: "5:00 PM",  duration: "30–45 min", personal: true },
      ...toddlerBedtime(),
      ...windDown(true, false),
    ],
  },

  Friday: {
    emoji: "🎉", theme: "Weekend Ready",
    tasks: [
      ...morningRoutine(true),
      { cat: "💪 Workout",        label: "Stroller walk (your cardio)",            time: "9:00 AM",  duration: "20–30 min", personal: true },
      { cat: "🖥 Office",         label: "Wipe desk & surfaces, vacuum floor, organize papers & clutter", time: "9:30 AM", duration: "~25 min" },
      { cat: "🌱 Personal Growth", label: "Personal growth time — course, hobby, reading or passion project", time: "11:00 AM", duration: "30 min", personal: true },
      ...mealsBlock(),
      ...napBlock(),
      { cat: "🏠 Home Mgmt",      label: "Plan next week's meals",                 time: "1:30 PM",  duration: "~20 min" },
      { cat: "🏠 Home Mgmt",      label: "Build grocery list",                     time: "1:50 PM",  duration: "~10 min" },
      { cat: "💇 Hair",           label: "Wash husband's hair",                    time: "4:00 PM",  duration: "~20 min", personal: true },
      { cat: "💪 Workout",        label: "30-min workout",                         time: "4:30 PM",  duration: "30 min", personal: true },
      { cat: "🌳 Outdoor Play",   label: "Outside time with toddler",              time: "5:00 PM",  duration: "30–45 min", personal: true },
      ...toddlerBedtime(),
      ...windDown(false, true),
    ],
  },

  Saturday: {
    emoji: "🛒", theme: "Prep & Admin Day",
    tasks: [
      ...weekendMorning(),
      { cat: "💪 Workout",         label: "Stroller walk (your cardio)",           time: "8:30 AM",  duration: "20–30 min", personal: true },
      { cat: "💪 Workout",         label: "30-min workout",                        time: "9:00 AM",  duration: "30 min", personal: true },
      { cat: "🏠 Home Mgmt",       label: "Grocery run for the week",              time: "10:00 AM", duration: "~1 hr" },
      ...mealsBlock(),
      ...napBlock(),
      { cat: "🧺 Laundry",         label: "Towels & bedding laundry — start, swap & fold", time: "12:30 PM", duration: "~45 min" },
      { cat: "💆 Self-Care",       label: "Nails, grooming or pamper time for you", time: "1:30 PM",  duration: "30–60 min", personal: true },
      { cat: "🏠 Home Mgmt",       label: "Check upcoming appts / schedule if needed", time: "2:00 PM", duration: "~10 min" },
      { cat: "🏠 Home Mgmt",       label: "Handle any home errands or repairs",     time: "2:10 PM",  duration: "~10 min" },
      { cat: "🏠 Home Mgmt",       label: "Budget review & bank account check",     time: "2:20 PM",  duration: "~10 min" },
      { cat: "🌳 Outdoor Play",    label: "Outside time with toddler",              time: "3:00 PM",  duration: "30–45 min", personal: true },
      { cat: "🔗 Connection",      label: "Call grandmother",                       time: "4:00 PM",  duration: "20–30 min", personal: true },
      ...toddlerBedtime(),
      { cat: "🔗 Connection",      label: "Date night — dinner out or in together", time: "8:00 PM",  duration: "1–2 hrs", personal: true },
      { cat: "🌙 Evening Wind-Down", label: "Skincare night routine",               time: "10:00 PM", duration: "10 min", personal: true },
      { cat: "🌙 Evening Wind-Down", label: "Lights out",                           time: "11:00 PM", duration: "—" },
    ],
  },

  Sunday: {
    emoji: "🍲", theme: "Nourish & Reset",
    tasks: [
      ...weekendMorning(),
      { cat: "💪 Workout",      label: "Stroller walk & best friend call",       time: "9:30 AM",  duration: "20–30 min", personal: true },
      { cat: "👶 Toddler Dev",  label: "Wash toddler's hair",                    time: "10:00 AM", duration: "~20 min" },
      ...mealsBlock(),
      { cat: "😴 Toddler Nap",  label: "Toddler nap begins",                     time: "12:00 PM", duration: "2.5 hrs" },
      { cat: "🧺 Laundry",      label: "Your laundry — start, swap & fold",      time: "12:30 PM", duration: "~45 min" },
      { cat: "😴 Toddler Nap",  label: "Toddler wakes",                          time: "2:30 PM",  duration: "—" },
      { cat: "🌳 Outdoor Play", label: "Outside time with toddler",              time: "3:00 PM",  duration: "30–45 min", personal: true },
      { cat: "🏠 Home Mgmt",    label: "Weekly meal prep — prep, portion & store", time: "3:00 PM",  duration: "~1.5 hrs" },
      { cat: "🔗 Connection",   label: "Toddler family video call",              time: "5:00 PM",  duration: "15–20 min", personal: true },
      ...toddlerBedtime(),
      { cat: "✅ Reset",        label: "Quick tidy — kitchen & common areas",    time: "8:00 PM",  duration: "~15 min" },
      { cat: "🏠 Home Mgmt",    label: "Set out clothes & bag for Monday",       time: "8:15 PM",  duration: "~5 min" },
      { cat: "🔗 Connection",   label: "Weekly couple check-in — align on the week ahead, share needs", time: "8:30 PM", duration: "15 min", personal: true },
      { cat: "✅ Reset",        label: "Review the week ahead — mental prep",    time: "8:45 PM",  duration: "~10 min" },
      { cat: "🌙 Evening Wind-Down", label: "Alone time — read, journal or decompress", time: "9:00 PM", duration: "30–45 min", personal: true },
      { cat: "🌙 Evening Wind-Down", label: "Skincare night routine",            time: "10:00 PM", duration: "10 min", personal: true },
      { cat: "🌙 Evening Wind-Down", label: "Lights out",                        time: "11:00 PM", duration: "—" },
    ],
  },
};

/**
 * Stamp each task with a stable id of `${dayPrefix}${index}` and sort by time
 * within each day. These IDs are the keys in the Firestore `checked` maps —
 * adding/reordering tasks above must preserve key stability where possible.
 */
export const schedule: Record<FullDay, DaySchedule> = (() => {
  const out = {} as Record<FullDay, DaySchedule>;
  for (const day of FULL_DAYS) {
    const sorted = [...rawSchedule[day].tasks]
      .map((t, i): Task => ({ ...t, id: `${day.slice(0, 2)}${i}` }))
      .sort((a, b) => timeToMins(a.time) - timeToMins(b.time));
    out[day] = { ...rawSchedule[day], tasks: sorted };
  }
  return out;
})();
